import React, { useState } from "react";
import { X, Upload, Trash2, ImageIcon, AlertCircle, Cpu, HardDrive, Monitor, Palette, Smartphone, Star } from "lucide-react";
import { useTheme } from "../../components/Theme/ThemeProvider";
import productCategory from "../../helpers/productCategory";
import uploadImage from "../../helpers/uploadImage";
import DisplayImage from "./DisplayImage";
import { summaryApi } from "../../common";
import { toast } from "react-toastify";
import { authFetch } from '../../helpers/authFetch';

function UploadProducts({ onClose, fetchData }) {
    const { colors } = useTheme();
    const [data, setData] = useState({
        productName: "",
        productImage: [],
        productBrand: "",
        productPrice: "",
        productsCategory: "",
        productDiscount: "",
        productDescription: "",
        storage: "",
        color: "",
        ram: "",
        processor: "",
        screenSize: "",
        featured: false
    });

    const [uploadProductImageInput, setUploadProductImageInput] = useState("");
    const [fullScreenImage, setFullScreenImage] = useState("");
    const [openFullScreenImage, setOpenFullScreenImage] = useState(false);
    const [imagePreview, setImagePreview] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleOnChange = (e) => {
        const { name, value } = e.target;
        setData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setData((prev) => ({
        ...prev,
        [name]: checked,
    }));
};

    const handleUploadProduct = async (e) => {
        try {
            const file = e.target.files[0];
            if (!file) return;

            setIsUploading(true);
            setUploadProductImageInput(file.name);

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);

            const uploadResult = await uploadImage(file);

            setData((prev) => ({
                ...prev,
                productImage: [...prev.productImage, uploadResult.secure_url],
            }));
            
            setTimeout(() => setImagePreview(""), 2000);
            toast.success("Image uploaded successfully");
        } catch (error) {
            console.error("Upload failed:", error);
            toast.error("Failed to upload image. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteProductImage = (index) => {
        const confirmDelete = window.confirm(
            "Are you sure you want to remove this image?"
        );

        if (!confirmDelete) return;

        const newProductImage = [...data.productImage];
        newProductImage.splice(index, 1);
        
        setData((prev) => ({
            ...prev,
            productImage: [...newProductImage],
        }));

        toast.success("Image removed successfully");
    };

    const handleSubmit = async(e) => {
        e.preventDefault();

        if (data.productImage.length === 0) {
            toast.error("Please upload at least one product image");
            return;
        }

        if (!data.productName || !data.productBrand || !data.productPrice || !data.productsCategory || data.productDiscount === '') {
            toast.error("Please fill in all required fields");
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await authFetch(summaryApi.uploadProduct.url,{
                method: summaryApi.uploadProduct.method,
                
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            });
            const responseData = await response.json();

            if(responseData.success){
                toast.success(responseData?.message);
                onClose();
                fetchData();            
            } else {
                toast.error(responseData?.message);            
            }
        } catch (error) {
            console.error("Upload error:", error);
            toast.error("Failed to upload product");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
            onClick={onClose}
        >
            <div 
                className="rounded-2xl shadow-2xl w-full max-w-3xl transform transition-all duration-300"
                style={{
                    backgroundColor: colors.bg,
                    border: `1px solid ${colors.border}`,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div 
                    className="flex items-center justify-between p-6 border-b"
                    style={{ borderColor: colors.border }}
                >
                    <h2 
                        className="text-2xl font-bold"
                        style={{ color: colors.text }}
                    >
                        Upload New Product
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg transition-all hover:scale-110"
                        style={{
                            backgroundColor: colors.bgSecondary,
                            color: colors.text,
                        }}
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Form Content */}
                <form onSubmit={handleSubmit} className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                    <div className="space-y-6">
                        {/* Product Name */}
                        <div>
                            <label 
                                className="block text-sm font-semibold mb-2"
                                style={{ color: colors.text }}
                            >
                                Product Name *
                            </label>
                            <input
                                type="text"
                                placeholder="Enter product name"
                                value={data?.productName}
                                onChange={handleOnChange}
                                name="productName"
                                required
                                className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
                                style={{
                                    backgroundColor: colors.bgSecondary,
                                    color: colors.text,
                                    border: `2px solid ${colors.border}`,
                                    boxShadow: data.productName ? `0 0 0 3px ${colors.primary}20` : 'none'
                                }}
                            />
                        </div>

                        {/* Product Images */}
                        <div>
                            <label 
                                className="block text-sm font-semibold mb-2"
                                style={{ color: colors.text }}
                            >
                                Product Images * (Upload at least 1)
                            </label>
                            
                            <label htmlFor="uploadImageInput">
                                <div 
                                    className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all hover:shadow-lg"
                                    style={{
                                        borderColor: isUploading ? colors.primary : colors.border,
                                        backgroundColor: colors.bgSecondary,
                                    }}
                                >
                                    <Upload className="w-12 h-12 mx-auto mb-3" style={{ color: colors.primary }} />
                                    <p 
                                        className="text-sm font-medium mb-1"
                                        style={{ color: colors.text }}
                                    >
                                        {isUploading ? "Uploading..." : "Click to upload images"}
                                    </p>
                                    <p 
                                        className="text-xs"
                                        style={{ color: colors.textSecondary }}
                                    >
                                        PNG, JPG up to 10MB
                                    </p>
                                    <input
                                        type="file"
                                        id="uploadImageInput"
                                        className="hidden"
                                        onChange={handleUploadProduct}
                                        accept="image/*"
                                        disabled={isUploading}
                                    />
                                </div>
                            </label>

                            {imagePreview && (
                                <div 
                                    className="mt-4 flex items-center space-x-3 p-4 rounded-lg"
                                    style={{ backgroundColor: `${colors.primary}20` }}
                                >
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        className="w-16 h-16 object-cover rounded"
                                    />
                                    <span 
                                        className="text-sm font-medium"
                                        style={{ color: colors.primary }}
                                    >
                                        ✓ Uploading...
                                    </span>
                                </div>
                            )}
                            
                            {data?.productImage[0] && (
                                <div className="mt-4 grid grid-cols-4 gap-4">
                                    {data.productImage.map((image, index) => (
                                        <div key={`${image}-${index}`} className="relative group">
                                            <img
                                                src={image}
                                                alt="Product"
                                                onClick={() => {
                                                    setOpenFullScreenImage(true);
                                                    setFullScreenImage(image);
                                                }}
                                                className="w-full aspect-square object-cover rounded-lg cursor-pointer transition-transform hover:scale-110"
                                                style={{ border: `1px solid ${colors.border}` }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteProductImage(index)}
                                                className="absolute top-2 right-2 p-2 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-all"
                                                style={{ backgroundColor: colors.accent }}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Brand and Price */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label 
                                    className="block text-sm font-semibold mb-2"
                                    style={{ color: colors.text }}
                                >
                                    Brand *
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter brand"
                                    value={data.productBrand}
                                    name="productBrand"
                                    onChange={handleOnChange}
                                    required
                                    className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
                                    style={{
                                        backgroundColor: colors.bgSecondary,
                                        color: colors.text,
                                        border: `2px solid ${colors.border}`
                                    }}
                                />
                            </div>

                            <div>
                                <label 
                                    className="block text-sm font-semibold mb-2"
                                    style={{ color: colors.text }}
                                >
                                    Price *
                                </label>
                                <input
                                    type="number"
                                    placeholder="Enter price"
                                    value={data.productPrice}
                                    name="productPrice"
                                    onChange={handleOnChange}
                                    required
                                    className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
                                    style={{
                                        backgroundColor: colors.bgSecondary,
                                        color: colors.text,
                                        border: `2px solid ${colors.border}`
                                    }}
                                />
                            </div>
                        </div>

                        {/* Category and Discount */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label 
                                    className="block text-sm font-semibold mb-2"
                                    style={{ color: colors.text }}
                                >
                                    Category *
                                </label>
                                <select
                                    value={data.productsCategory}
                                    onChange={handleOnChange}
                                    name="productsCategory"
                                    required
                                    className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
                                    style={{
                                        backgroundColor: colors.bgSecondary,
                                        color: colors.text,
                                        border: `2px solid ${colors.border}`
                                    }}
                                >
                                    <option value="">Select Category</option>
                                    {productCategory.map((item, index) => (
                                        <option key={item.value + index} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label 
                                    className="block text-sm font-semibold mb-2"
                                    style={{ color: colors.text }}
                                >
                                    Discount Amount *
                                </label>
                                <input
                                    type="number"
                                    placeholder="Enter discount amount"
                                    value={data.productDiscount}
                                    name="productDiscount"
                                    onChange={handleOnChange}
                                    required
                                    className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
                                    style={{
                                        backgroundColor: colors.bgSecondary,
                                        color: colors.text,
                                        border: `2px solid ${colors.border}`
                                    }}
                                />
                            </div>
                        </div>

                        <div 
                            className="p-4 rounded-lg border-2"
                            style={{ 
                                borderColor: colors.primary,
                                backgroundColor: `${colors.primary}10`
                            }}
                        >
                            <h3 
                                className="text-lg font-bold mb-4 flex items-center gap-2"
                                style={{ color: colors.primary }}
                            >
                                <Smartphone className="w-5 h-5" />
                                Product Specifications (Optional)
                            </h3>
                            
                            <div className="grid grid-cols-2 gap-4">
                                {/* Storage */}
                                <div>
                                    <label 
                                        className=" text-sm font-semibold mb-2 flex items-center gap-2"
                                        style={{ color: colors.text }}
                                    >
                                        <HardDrive className="w-4 h-4" />
                                        Storage
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., 128GB, 256GB"
                                        value={data.storage}
                                        name="storage"
                                        onChange={handleOnChange}
                                        className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
                                        style={{
                                            backgroundColor: colors.bgSecondary,
                                            color: colors.text,
                                            border: `2px solid ${colors.border}`
                                        }}
                                    />
                                </div>

                                {/* Color */}
                                <div>
                                    <label 
                                        className=" text-sm font-semibold mb-2 flex items-center gap-2"
                                        style={{ color: colors.text }}
                                    >
                                        <Palette className="w-4 h-4" />
                                        Color
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Black, White, Blue"
                                        value={data.color}
                                        name="color"
                                        onChange={handleOnChange}
                                        className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
                                        style={{
                                            backgroundColor: colors.bgSecondary,
                                            color: colors.text,
                                            border: `2px solid ${colors.border}`
                                        }}
                                    />
                                </div>

                                {/* RAM */}
                                <div>
                                    <label 
                                        className=" text-sm font-semibold mb-2 flex items-center gap-2"
                                        style={{ color: colors.text }}
                                    >
                                        <Cpu className="w-4 h-4" />
                                        RAM
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., 8GB, 16GB"
                                        value={data.ram}
                                        name="ram"
                                        onChange={handleOnChange}
                                        className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
                                        style={{
                                            backgroundColor: colors.bgSecondary,
                                            color: colors.text,
                                            border: `2px solid ${colors.border}`
                                        }}
                                    />
                                </div>

                                {/* Processor */}
                                <div>
                                    <label 
                                        className=" text-sm font-semibold mb-2 flex items-center gap-2"
                                        style={{ color: colors.text }}
                                    >
                                        <Cpu className="w-4 h-4" />
                                        Processor
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., A17 Pro, Snapdragon 8"
                                        value={data.processor}
                                        name="processor"
                                        onChange={handleOnChange}
                                        className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
                                        style={{
                                            backgroundColor: colors.bgSecondary,
                                            color: colors.text,
                                            border: `2px solid ${colors.border}`
                                        }}
                                    />
                                </div>

                                {/* Screen Size */}
                                <div className="col-span-2">
                                    <label 
                                        className=" text-sm font-semibold mb-2 flex items-center gap-2"
                                        style={{ color: colors.text }}
                                    >
                                        <Monitor className="w-4 h-4" />
                                        Screen Size
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., 6.1 inches, 13.6 inches"
                                        value={data.screenSize}
                                        name="screenSize"
                                        onChange={handleOnChange}
                                        className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
                                        style={{
                                            backgroundColor: colors.bgSecondary,
                                            color: colors.text,
                                            border: `2px solid ${colors.border}`
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Featured Product Checkbox */}
                        <div 
                            className="p-4 rounded-lg flex items-center gap-3"
                            style={{ 
                                backgroundColor: `${colors.accent}15`,
                                border: `2px solid ${colors.accent}`
                            }}
                        >
                            <input
                                type="checkbox"
                                id="featured"
                                name="featured"
                                checked={data.featured}
                                onChange={handleCheckboxChange}
                                className="w-5 h-5 rounded cursor-pointer"
                                style={{ accentColor: colors.accent }}
                            />
                            <label 
                                htmlFor="featured"
                                className="flex items-center gap-2 text-sm font-semibold cursor-pointer"
                                style={{ color: colors.text }}
                            >
                                <Star className="w-5 h-5" style={{ color: colors.accent }} />
                                Mark as Featured Product (Will appear on landing page)
                            </label>
                        </div>

                        {/* Description */}
                        <div>
                            <label 
                                className="block text-sm font-semibold mb-2"
                                style={{ color: colors.text }}
                            >
                                Description
                            </label>
                            <textarea
                                placeholder="Enter product description"
                                value={data.productDescription}
                                name="productDescription"
                                onChange={handleOnChange}
                                rows={4}
                                className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg resize-none"
                                style={{
                                    backgroundColor: colors.bgSecondary,
                                    color: colors.text,
                                    border: `2px solid ${colors.border}`
                                }}
                            />
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div 
                    className="flex space-x-3 p-6 border-t"
                    style={{ borderColor: colors.border }}
                >
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 px-6 py-3 font-semibold rounded-lg transition-all hover:scale-105"
                        style={{
                            backgroundColor: colors.bgSecondary,
                            color: colors.text,
                            border: `1px solid ${colors.border}`
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={isUploading || data.productImage.length === 0 || isSubmitting}
                        className="flex-1 px-6 py-3 font-semibold text-white rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                        }}
                    >
                        {isSubmitting ? 'Uploading...' : 'Upload Product'}
                    </button>
                </div>
            </div>
            
            {openFullScreenImage && (
                <DisplayImage
                    onClose={() => setOpenFullScreenImage(false)}
                    imgUrl={fullScreenImage}
                />
            )}
        </div>
    );
}

export default UploadProducts;

// import React, { useState } from "react";
// import { X, Upload, Trash2, ImageIcon, AlertCircle, Cpu, HardDrive, Monitor, Palette, Smartphone, Star } from "lucide-react";
// import { useTheme } from "../../components/Theme/ThemeProvider";
// import uploadImage from "../../helpers/uploadImage";
// import DisplayImage from "./DisplayImage";
// import { summaryApi } from "../../common";
// import { toast } from "react-toastify";

// // Define categories directly in component
// const productCategories = [
//     {
//         id: 1,
//         label: "Smart Phones",
//         value: "smartphones",
//         subcategories: [
//             { id: 11, label: "iPhone", value: "iphone" },
//             { id: 12, label: "Samsung", value: "samsung" },
//             { id: 13, label: "Oppo", value: "oppo" },
//             { id: 14, label: "Infinix", value: "infinix" },
//             { id: 15, label: "Google Pixel", value: "google-pixel" }
//         ]
//     },
//     {
//         id: 2,
//         label: "Tablets",
//         value: "tablets",
//         subcategories: [
//             { id: 21, label: "iPads", value: "ipads" },
//             { id: 22, label: "Samsung Tablets", value: "samsung-tablets" },
//             { id: 23, label: "Xiomi Tabs", value: "xiomi-tabs" }
//         ]
//     },
//     {
//         id: 3,
//         label: "Smart Watches",
//         value: "smart-watches",
//         subcategories: [
//             { id: 31, label: "iWatches", value: "iwatches" },
//             { id: 32, label: "Samsung Watches", value: "samsung-watches" }
//         ]
//     },
//     {
//         id: 4,
//         label: "Laptops",
//         value: "laptops",
//         subcategories: [
//             { id: 41, label: "Apple Laptop", value: "apple-laptop" },
//             { id: 42, label: "Microsoft Surface", value: "microsoft-surface" },
//             { id: 43, label: "HP", value: "hp" },
//             { id: 44, label: "Dell", value: "dell" },
//             { id: 45, label: "Lenovo", value: "lenovo" },
//             { id: 46, label: "MSI", value: "msi" }
//         ]
//     },
//     {
//         id: 5,
//         label: "Drones",
//         value: "drones",
//         subcategories: [
//             { id: 51, label: "DJI Drones", value: "dji-drones" }
//         ]
//     },
//     {
//         id: 6,
//         label: "Accessories",
//         value: "accessories",
//         subcategories: [
//             { id: 61, label: "Chargers", value: "chargers" },
//             { id: 62, label: "Powerbanks", value: "powerbanks" }
//         ]
//     },
//     {
//         id: 7,
//         label: "Games Consoles",
//         value: "games-consoles",
//         subcategories: [
//             { id: 71, label: "PlayStation", value: "playstation" },
//             { id: 72, label: "Xbox", value: "xbox" },
//             { id: 73, label: "Meta Quest VR", value: "meta-quest" },
//             { id: 74, label: "Nintendo Switch", value: "nintendo-switch" }
//         ]
//     }
// ];

// function UploadProducts({ onClose, fetchData }) {
//     const { colors } = useTheme();
//     const [data, setData] = useState({
//         productName: "",
//         productImage: [],
//         productBrand: "",
//         productPrice: "",
//         productsCategory: "",
//         productDiscount: "",
//         productDescription: "",
//         storage: "",
//         color: "",
//         ram: "",
//         processor: "",
//         screenSize: "",
//         featured: false
//     });

//     const [uploadProductImageInput, setUploadProductImageInput] = useState("");
//     const [fullScreenImage, setFullScreenImage] = useState("");
//     const [openFullScreenImage, setOpenFullScreenImage] = useState(false);
//     const [imagePreview, setImagePreview] = useState("");
//     const [isUploading, setIsUploading] = useState(false);
//     const [isSubmitting, setIsSubmitting] = useState(false);

//     const handleOnChange = (e) => {
//         const { name, value } = e.target;
//         setData((prev) => ({
//             ...prev,
//             [name]: value,
//         }));
//     };

//     const handleCheckboxChange = (e) => {
//         const { name, checked } = e.target;
//         setData((prev) => ({
//             ...prev,
//             [name]: checked,
//         }));
//     };

//     const handleUploadProduct = async (e) => {
//         try {
//             const file = e.target.files[0];
//             if (!file) return;

//             setIsUploading(true);
//             setUploadProductImageInput(file.name);

//             const reader = new FileReader();
//             reader.onloadend = () => {
//                 setImagePreview(reader.result);
//             };
//             reader.readAsDataURL(file);

//             const uploadResult = await uploadImage(file);

//             setData((prev) => ({
//                 ...prev,
//                 productImage: [...prev.productImage, uploadResult.secure_url],
//             }));
            
//             setTimeout(() => setImagePreview(""), 2000);
//             toast.success("Image uploaded successfully");
//         } catch (error) {
//             console.error("Upload failed:", error);
//             toast.error("Failed to upload image. Please try again.");
//         } finally {
//             setIsUploading(false);
//         }
//     };

//     const handleDeleteProductImage = (index) => {
//         const confirmDelete = window.confirm(
//             "Are you sure you want to remove this image?"
//         );

//         if (!confirmDelete) return;

//         const newProductImage = [...data.productImage];
//         newProductImage.splice(index, 1);
        
//         setData((prev) => ({
//             ...prev,
//             productImage: [...newProductImage],
//         }));

//         toast.success("Image removed successfully");
//     };

//     const handleSubmit = async(e) => {
//         e.preventDefault();

//         if (data.productImage.length === 0) {
//             toast.error("Please upload at least one product image");
//             return;
//         }

//         if (!data.productName || !data.productBrand || !data.productPrice || !data.productsCategory || data.productDiscount === '') {
//             toast.error("Please fill in all required fields");
//             return;
//         }

//         try {
//             setIsSubmitting(true);
//             const response = await fetch(summaryApi.uploadProduct.url,{
//                 method: summaryApi.uploadProduct.method,
//                 credentials: "include",
//                 headers: {
//                     'Content-Type': 'application/json',
//                 },
//                 body: JSON.stringify(data)
//             });
//             const responseData = await response.json();

//             if(responseData.success){
//                 toast.success(responseData?.message);
//                 onClose();
//                 fetchData();            
//             } else {
//                 toast.error(responseData?.message);            
//             }
//         } catch (error) {
//             console.error("Upload error:", error);
//             toast.error("Failed to upload product");
//         } finally {
//             setIsSubmitting(false);
//         }
//     };

//     return (
//         <div 
//             className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
//             style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
//             onClick={onClose}
//         >
//             <div 
//                 className="rounded-2xl shadow-2xl w-full max-w-3xl transform transition-all duration-300"
//                 style={{
//                     backgroundColor: colors.bg,
//                     border: `1px solid ${colors.border}`,
//                 }}
//                 onClick={(e) => e.stopPropagation()}
//             >
//                 {/* Header */}
//                 <div 
//                     className="flex items-center justify-between p-6 border-b"
//                     style={{ borderColor: colors.border }}
//                 >
//                     <h2 
//                         className="text-2xl font-bold"
//                         style={{ color: colors.text }}
//                     >
//                         Upload New Product
//                     </h2>
//                     <button
//                         onClick={onClose}
//                         className="p-2 rounded-lg transition-all hover:scale-110"
//                         style={{
//                             backgroundColor: colors.bgSecondary,
//                             color: colors.text,
//                         }}
//                     >
//                         <X className="w-6 h-6" />
//                     </button>
//                 </div>

//                 {/* Form Content */}
//                 <form onSubmit={handleSubmit} className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
//                     <div className="space-y-6">
//                         {/* Product Name */}
//                         <div>
//                             <label 
//                                 className="block text-sm font-semibold mb-2"
//                                 style={{ color: colors.text }}
//                             >
//                                 Product Name *
//                             </label>
//                             <input
//                                 type="text"
//                                 placeholder="Enter product name"
//                                 value={data?.productName}
//                                 onChange={handleOnChange}
//                                 name="productName"
//                                 required
//                                 className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
//                                 style={{
//                                     backgroundColor: colors.bgSecondary,
//                                     color: colors.text,
//                                     border: `2px solid ${colors.border}`,
//                                     boxShadow: data.productName ? `0 0 0 3px ${colors.primary}20` : 'none'
//                                 }}
//                             />
//                         </div>

//                         {/* Product Images */}
//                         <div>
//                             <label 
//                                 className="block text-sm font-semibold mb-2"
//                                 style={{ color: colors.text }}
//                             >
//                                 Product Images * (Upload at least 1)
//                             </label>
                            
//                             <label htmlFor="uploadImageInput">
//                                 <div 
//                                     className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all hover:shadow-lg"
//                                     style={{
//                                         borderColor: isUploading ? colors.primary : colors.border,
//                                         backgroundColor: colors.bgSecondary,
//                                     }}
//                                 >
//                                     <Upload className="w-12 h-12 mx-auto mb-3" style={{ color: colors.primary }} />
//                                     <p 
//                                         className="text-sm font-medium mb-1"
//                                         style={{ color: colors.text }}
//                                     >
//                                         {isUploading ? "Uploading..." : "Click to upload images"}
//                                     </p>
//                                     <p 
//                                         className="text-xs"
//                                         style={{ color: colors.textSecondary }}
//                                     >
//                                         PNG, JPG up to 10MB
//                                     </p>
//                                     <input
//                                         type="file"
//                                         id="uploadImageInput"
//                                         className="hidden"
//                                         onChange={handleUploadProduct}
//                                         accept="image/*"
//                                         disabled={isUploading}
//                                     />
//                                 </div>
//                             </label>

//                             {imagePreview && (
//                                 <div 
//                                     className="mt-4 flex items-center space-x-3 p-4 rounded-lg"
//                                     style={{ backgroundColor: `${colors.primary}20` }}
//                                 >
//                                     <img
//                                         src={imagePreview}
//                                         alt="Preview"
//                                         className="w-16 h-16 object-cover rounded"
//                                     />
//                                     <span 
//                                         className="text-sm font-medium"
//                                         style={{ color: colors.primary }}
//                                     >
//                                         ✓ Uploading...
//                                     </span>
//                                 </div>
//                             )}
                            
//                             {data?.productImage[0] && (
//                                 <div className="mt-4 grid grid-cols-4 gap-4">
//                                     {data.productImage.map((image, index) => (
//                                         <div key={`${image}-${index}`} className="relative group">
//                                             <img
//                                                 src={image}
//                                                 alt="Product"
//                                                 onClick={() => {
//                                                     setOpenFullScreenImage(true);
//                                                     setFullScreenImage(image);
//                                                 }}
//                                                 className="w-full aspect-square object-cover rounded-lg cursor-pointer transition-transform hover:scale-110"
//                                                 style={{ border: `1px solid ${colors.border}` }}
//                                             />
//                                             <button
//                                                 type="button"
//                                                 onClick={() => handleDeleteProductImage(index)}
//                                                 className="absolute top-2 right-2 p-2 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-all"
//                                                 style={{ backgroundColor: colors.accent }}
//                                             >
//                                                 <Trash2 className="w-4 h-4" />
//                                             </button>
//                                         </div>
//                                     ))}
//                                 </div>
//                             )}
//                         </div>

//                         {/* Brand and Price */}
//                         <div className="grid grid-cols-2 gap-4">
//                             <div>
//                                 <label 
//                                     className="block text-sm font-semibold mb-2"
//                                     style={{ color: colors.text }}
//                                 >
//                                     Brand *
//                                 </label>
//                                 <input
//                                     type="text"
//                                     placeholder="Enter brand"
//                                     value={data.productBrand}
//                                     name="productBrand"
//                                     onChange={handleOnChange}
//                                     required
//                                     className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
//                                     style={{
//                                         backgroundColor: colors.bgSecondary,
//                                         color: colors.text,
//                                         border: `2px solid ${colors.border}`
//                                     }}
//                                 />
//                             </div>

//                             <div>
//                                 <label 
//                                     className="block text-sm font-semibold mb-2"
//                                     style={{ color: colors.text }}
//                                 >
//                                     Price *
//                                 </label>
//                                 <input
//                                     type="number"
//                                     placeholder="Enter price"
//                                     value={data.productPrice}
//                                     name="productPrice"
//                                     onChange={handleOnChange}
//                                     required
//                                     className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
//                                     style={{
//                                         backgroundColor: colors.bgSecondary,
//                                         color: colors.text,
//                                         border: `2px solid ${colors.border}`
//                                     }}
//                                 />
//                             </div>
//                         </div>

//                         {/* Category and Discount */}
//                         <div className="grid grid-cols-2 gap-4">
//                             <div>
//                                 <label 
//                                     className="block text-sm font-semibold mb-2"
//                                     style={{ color: colors.text }}
//                                 >
//                                     Category * (Select Subcategory)
//                                 </label>
//                                 <select
//                                     value={data.productsCategory}
//                                     onChange={handleOnChange}
//                                     name="productsCategory"
//                                     required
//                                     className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
//                                     style={{
//                                         backgroundColor: colors.bgSecondary,
//                                         color: colors.text,
//                                         border: `2px solid ${colors.border}`
//                                     }}
//                                 >
//                                     <option value="">Select Category</option>
//                                     {productCategories.map((category) => (
//                                         <optgroup key={category.id} label={category.label}>
//                                             {category.subcategories && category.subcategories.map((sub) => (
//                                                 <option key={sub.id} value={sub.value}>
//                                                     {sub.label}
//                                                 </option>
//                                             ))}
//                                         </optgroup>
//                                     ))}
//                                 </select>
//                             </div>

//                             <div>
//                                 <label 
//                                     className="block text-sm font-semibold mb-2"
//                                     style={{ color: colors.text }}
//                                 >
//                                     Discount Amount *
//                                 </label>
//                                 <input
//                                     type="number"
//                                     placeholder="Enter discount amount"
//                                     value={data.productDiscount}
//                                     name="productDiscount"
//                                     onChange={handleOnChange}
//                                     required
//                                     className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
//                                     style={{
//                                         backgroundColor: colors.bgSecondary,
//                                         color: colors.text,
//                                         border: `2px solid ${colors.border}`
//                                     }}
//                                 />
//                             </div>
//                         </div>

//                         <div 
//                             className="p-4 rounded-lg border-2"
//                             style={{ 
//                                 borderColor: colors.primary,
//                                 backgroundColor: `${colors.primary}10`
//                             }}
//                         >
//                             <h3 
//                                 className="text-lg font-bold mb-4 flex items-center gap-2"
//                                 style={{ color: colors.primary }}
//                             >
//                                 <Smartphone className="w-5 h-5" />
//                                 Product Specifications (Optional)
//                             </h3>
                            
//                             <div className="grid grid-cols-2 gap-4">
//                                 {/* Storage */}
//                                 <div>
//                                     <label 
//                                         className="text-sm font-semibold mb-2 flex items-center gap-2"
//                                         style={{ color: colors.text }}
//                                     >
//                                         <HardDrive className="w-4 h-4" />
//                                         Storage
//                                     </label>
//                                     <input
//                                         type="text"
//                                         placeholder="e.g., 128GB, 256GB"
//                                         value={data.storage}
//                                         name="storage"
//                                         onChange={handleOnChange}
//                                         className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
//                                         style={{
//                                             backgroundColor: colors.bgSecondary,
//                                             color: colors.text,
//                                             border: `2px solid ${colors.border}`
//                                         }}
//                                     />
//                                 </div>

//                                 {/* Color */}
//                                 <div>
//                                     <label 
//                                         className="text-sm font-semibold mb-2 flex items-center gap-2"
//                                         style={{ color: colors.text }}
//                                     >
//                                         <Palette className="w-4 h-4" />
//                                         Color
//                                     </label>
//                                     <input
//                                         type="text"
//                                         placeholder="e.g., Black, White, Blue"
//                                         value={data.color}
//                                         name="color"
//                                         onChange={handleOnChange}
//                                         className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
//                                         style={{
//                                             backgroundColor: colors.bgSecondary,
//                                             color: colors.text,
//                                             border: `2px solid ${colors.border}`
//                                         }}
//                                     />
//                                 </div>

//                                 {/* RAM */}
//                                 <div>
//                                     <label 
//                                         className="text-sm font-semibold mb-2 flex items-center gap-2"
//                                         style={{ color: colors.text }}
//                                     >
//                                         <Cpu className="w-4 h-4" />
//                                         RAM
//                                     </label>
//                                     <input
//                                         type="text"
//                                         placeholder="e.g., 8GB, 16GB"
//                                         value={data.ram}
//                                         name="ram"
//                                         onChange={handleOnChange}
//                                         className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
//                                         style={{
//                                             backgroundColor: colors.bgSecondary,
//                                             color: colors.text,
//                                             border: `2px solid ${colors.border}`
//                                         }}
//                                     />
//                                 </div>

//                                 {/* Processor */}
//                                 <div>
//                                     <label 
//                                         className="text-sm font-semibold mb-2 flex items-center gap-2"
//                                         style={{ color: colors.text }}
//                                     >
//                                         <Cpu className="w-4 h-4" />
//                                         Processor
//                                     </label>
//                                     <input
//                                         type="text"
//                                         placeholder="e.g., A17 Pro, Snapdragon 8"
//                                         value={data.processor}
//                                         name="processor"
//                                         onChange={handleOnChange}
//                                         className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
//                                         style={{
//                                             backgroundColor: colors.bgSecondary,
//                                             color: colors.text,
//                                             border: `2px solid ${colors.border}`
//                                         }}
//                                     />
//                                 </div>

//                                 {/* Screen Size */}
//                                 <div className="col-span-2">
//                                     <label 
//                                         className="text-sm font-semibold mb-2 flex items-center gap-2"
//                                         style={{ color: colors.text }}
//                                     >
//                                         <Monitor className="w-4 h-4" />
//                                         Screen Size
//                                     </label>
//                                     <input
//                                         type="text"
//                                         placeholder="e.g., 6.1 inches, 13.6 inches"
//                                         value={data.screenSize}
//                                         name="screenSize"
//                                         onChange={handleOnChange}
//                                         className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg"
//                                         style={{
//                                             backgroundColor: colors.bgSecondary,
//                                             color: colors.text,
//                                             border: `2px solid ${colors.border}`
//                                         }}
//                                     />
//                                 </div>
//                             </div>
//                         </div>

//                         {/* Featured Product Checkbox */}
//                         <div 
//                             className="p-4 rounded-lg flex items-center gap-3"
//                             style={{ 
//                                 backgroundColor: `${colors.accent}15`,
//                                 border: `2px solid ${colors.accent}`
//                             }}
//                         >
//                             <input
//                                 type="checkbox"
//                                 id="featured"
//                                 name="featured"
//                                 checked={data.featured}
//                                 onChange={handleCheckboxChange}
//                                 className="w-5 h-5 rounded cursor-pointer"
//                                 style={{ accentColor: colors.accent }}
//                             />
//                             <label 
//                                 htmlFor="featured"
//                                 className="flex items-center gap-2 text-sm font-semibold cursor-pointer"
//                                 style={{ color: colors.text }}
//                             >
//                                 <Star className="w-5 h-5" style={{ color: colors.accent }} />
//                                 Mark as Featured Product (Will appear on landing page)
//                             </label>
//                         </div>

//                         {/* Description */}
//                         <div>
//                             <label 
//                                 className="block text-sm font-semibold mb-2"
//                                 style={{ color: colors.text }}
//                             >
//                                 Description
//                             </label>
//                             <textarea
//                                 placeholder="Enter product description"
//                                 value={data.productDescription}
//                                 name="productDescription"
//                                 onChange={handleOnChange}
//                                 rows={4}
//                                 className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:shadow-lg resize-none"
//                                 style={{
//                                     backgroundColor: colors.bgSecondary,
//                                     color: colors.text,
//                                     border: `2px solid ${colors.border}`
//                                 }}
//                             />
//                         </div>
//                     </div>
//                 </form>

//                 {/* Footer */}
//                 <div 
//                     className="flex space-x-3 p-6 border-t"
//                     style={{ borderColor: colors.border }}
//                 >
//                     <button
//                         type="button"
//                         onClick={onClose}
//                         className="flex-1 px-6 py-3 font-semibold rounded-lg transition-all hover:scale-105"
//                         style={{
//                             backgroundColor: colors.bgSecondary,
//                             color: colors.text,
//                             border: `1px solid ${colors.border}`
//                         }}
//                     >
//                         Cancel
//                     </button>
//                     <button
//                         type="submit"
//                         onClick={handleSubmit}
//                         disabled={isUploading || data.productImage.length === 0 || isSubmitting}
//                         className="flex-1 px-6 py-3 font-semibold text-white rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
//                         style={{
//                             background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
//                         }}
//                     >
//                         {isSubmitting ? 'Uploading...' : 'Upload Product'}
//                     </button>
//                 </div>
//             </div>
            
//             {openFullScreenImage && (
//                 <DisplayImage
//                     onClose={() => setOpenFullScreenImage(false)}
//                     imgUrl={fullScreenImage}
//                 />
//             )}
//         </div>
//     );
// }

// export default UploadProducts;